import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Scenario } from '../../lib/types';
import { getMillById } from '../../lib/millsData';
import { setStoredMillSelection } from '../../hooks/useMillSelection';

interface ScenarioCardProps {
  scenario: Scenario;
  // 'full' is the entry-point card shown in ExplorerView (step count, region,
  // sector, question, Start → cue). 'compact' is the "try another" affordance
  // on the insight step — same chrome + hover, but trimmed metadata so three
  // cards stack cleanly below an already-dense insight panel.
  variant?: 'full' | 'compact';
}

export const ScenarioCard: React.FC<ScenarioCardProps> = ({
  scenario,
  variant = 'full',
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    const mill = getMillById(scenario.millId);
    if (!mill) {
      console.error(`[ScenarioCard] mill not found for scenario ${scenario.id}`);
      return;
    }
    // Mirror the scenario's mill into the Explorer's persistence slot so a
    // later "Back to supply sheds" lands on dropdowns that match the shed
    // the visitor just walked through, not whatever was selected before.
    setStoredMillSelection(mill);
    navigate(`/detail/${mill.id}`, {
      state: { mill, scenarioId: scenario.id },
    });
  };

  if (variant === 'compact') {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="w-full text-left p-2.5 bg-figma-card rounded-figma-panel border border-figma-lime/30 shadow-figma-card hover:border-figma-yellow/60 focus:outline-none focus:border-figma-yellow transition-colors group"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg leading-none" aria-hidden="true">
            {scenario.emoji}
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-body-sm font-roboto font-bold text-figma-lime truncate">
              {scenario.name}
            </p>
            <p className="text-xs font-roboto text-neutral-500 truncate">
              {scenario.region}
            </p>
          </div>
          <span className="text-xs font-roboto-mono text-figma-yellow whitespace-nowrap">
            Start →
          </span>
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="w-full text-left p-3 bg-figma-card rounded-figma-panel border border-figma-lime/30 shadow-figma-card hover:border-figma-yellow/60 focus:outline-none focus:border-figma-yellow transition-colors group"
    >
      <div className="flex items-start gap-2">
        <span className="text-xl leading-none pt-0.5" aria-hidden="true">
          {scenario.emoji}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h4 className="text-body font-roboto font-bold text-figma-lime truncate">
              {scenario.name}
            </h4>
            <span className="text-xs font-roboto-mono text-neutral-500 whitespace-nowrap">
              {scenario.steps.length} steps
            </span>
          </div>
          <p className="mt-0.5 text-xs font-roboto text-neutral-400">
            {scenario.region}
          </p>
          <p className="mt-0.5 text-xs font-roboto text-neutral-500">
            {scenario.sector}
          </p>
          {scenario.synopsis && (
            <p className="mt-2 text-xs font-roboto text-figma-ink-soft leading-relaxed">
              {scenario.synopsis}
            </p>
          )}
          <p className="mt-2 text-xs font-roboto italic text-neutral-300">
            "{scenario.question}"
          </p>
          <p className="mt-2 text-xs font-roboto-mono text-figma-yellow group-hover:text-figma-yellow">
            Start →
          </p>
        </div>
      </div>
    </button>
  );
};
